import { useQuery } from '@apollo/client';
import { useMe } from '@crehana/web';
import { useRouter } from 'next/router';

import { UserOrganizationRoleEnum } from '@/shared/types/graphql/globals.v5';
import useLeadership from '@/views/AdminDashboard/hooks/useLeadership';

import { useCentralizedCorporateNavigation } from '../use-centralized-corporate-navigation';
import GetOrganizationState from './graphql/GetOrganizationState.b2b.graphql';
import type {
  GetOrganizationStateVariables,
  GetOrganizationState as IGetOrganizationState,
} from './graphql/types/GetOrganizationState';
import { replaceOrganizationSlug } from './utils';

enum ROLES {
  ROLE_ADMIN = 'admin',
  ROLE_OWNER = 'owner',
  ROLE_MEMBERS = 'member',
  ROLE_CORPORATION = 'owner-corporation',
}

const isProduction = process.env.NODE_ENV === 'production';

const onboarding = '/onboarding/welcome';

const usePermissions = ({ slug }: { slug: string }) => {
  const { push, pathname } = useRouter();
  const { loading: isLoadingMe, error: errorMe, me, talentUser } = useMe();
  const { isLeader, isLeaderPanel, isLoadingLeader } = useLeadership();

  const {
    userOrganizationSlug,
    isCentralizedCorporation,
    isCorporateSuperAdmin,
    isLoading: isLoadingCentralizedCorporateNavigation,
    error: errorCentralizedCorporateNavigation,
  } = useCentralizedCorporateNavigation(slug);

  const {
    data,
    loading: isLoadingOrg,
    error: errorOrg,
  } = useQuery<IGetOrganizationState, GetOrganizationStateVariables>(
    GetOrganizationState,
    {
      skip: !slug,
      variables: { slug },
      context: { clientName: 'b2b' },
    },
  );

  const userRoles = talentUser.roles;
  const isLoadingTalentUser = talentUser.loading;

  const isAdminUser =
    userRoles.includes(UserOrganizationRoleEnum.ADMIN) ||
    userRoles.includes(UserOrganizationRoleEnum.SUBADMIN);

  const userOrganization = me?.organization?.slug;

  const isOrganizationActive = data?.organization?.isActive;
  const parentSlug = data?.organization?.parentSlug;

  const isCorporation = me?.organization?.myRole === ROLES.ROLE_CORPORATION;
  const isStudent = me?.organization?.myRole === ROLES.ROLE_MEMBERS;
  const isAdmin = me?.organization?.myRole === ROLES.ROLE_ADMIN;
  const isOwner = me?.organization?.myRole === ROLES.ROLE_OWNER;

  const isUserOrganization = userOrganization === slug;
  const isStaff = me?.isStaff;

  const isStudentInLeadership = isStudent && isLeaderPanel;
  const isPermitStudent = isStudent ? isLeader && isStudentInLeadership : false;

  const showPanel =
    isAdmin ||
    isAdminUser ||
    (isOwner && isUserOrganization) ||
    (isCorporation &&
      (isUserOrganization || parentSlug === userOrganization)) ||
    isStaff ||
    isPermitStudent;

  const loading =
    isLoadingMe ||
    isLoadingOrg ||
    isLoadingLeader ||
    isLoadingTalentUser ||
    isLoadingCentralizedCorporateNavigation;

  const redirectToLogin = !me && !loading && isProduction;

  if (redirectToLogin) {
    const next = `${window.location.pathname}${window.location.search}`;

    push(`/org/${slug}/entrar/?next=${next}`);
    return {
      loading: true,
    };
  }

  if (!loading && isCentralizedCorporation) {
    // Users other than staff and corporate admins must be redirected
    // to their own organization
    const redirectToOwnOrganization = !isStaff && !isCorporateSuperAdmin;

    if (redirectToOwnOrganization) {
      const targetSlug = userOrganizationSlug || me?.organization?.slug;

      if (!targetSlug) {
        return {
          loading: false,
          error: new Error('Missing organization slug'),
        };
      }

      const newPathname = replaceOrganizationSlug(
        window.location.pathname,
        targetSlug,
      );

      if (newPathname !== window.location.pathname) {
        push(newPathname);
        return {
          loading: true,
        };
      }
    }
  }

  const isOnboarding = pathname.includes(onboarding);

  return {
    loading: isOnboarding ? false : loading,
    error: errorMe || errorOrg || errorCentralizedCorporateNavigation,
    showView: isOrganizationActive ? showPanel : isOnboarding,
    parentSlug,
  };
};

export default usePermissions;
